import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Trophy, CheckCircle2, XCircle, RefreshCw, Share2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import Seo from '@/components/Seo';

const QUESTIONS = [
  {
    q: 'What does "BHK" stand for in Indian real estate?',
    options: ['Bedroom, Hall, Kitchen', 'Bathroom, Hall, Kitchen', 'Building, House, Kitchen', 'Balcony, Hall, Kitchen'],
    answer: 0,
    explain: 'BHK stands for Bedroom, Hall, Kitchen. A 2BHK has 2 bedrooms + 1 hall + 1 kitchen.',
  },
  {
    q: 'What is the typical stamp duty range in most Indian states?',
    options: ['1–2% of property value', '4–8% of property value', '10–15% of property value', '20% of property value'],
    answer: 1,
    explain: 'Stamp duty in India typically ranges from 4% to 8% depending on the state, gender of buyer, and property type.',
  },
  {
    q: 'What does "carpet area" mean?',
    options: ['Total area including walls', 'Usable area inside walls', 'Area including balconies', 'Built-up area + common area'],
    answer: 1,
    explain: 'Carpet area is the actual usable area within the walls of the apartment — what you can lay a carpet on. It excludes wall thickness.',
  },
  {
    q: 'Which of these is NOT a typical home loan eligibility factor?',
    options: ['Monthly income', 'Credit score', 'Favourite color', 'Employment stability'],
    answer: 2,
    explain: 'Banks evaluate income, credit score (CIBIL), employment stability, age, and existing debts. Personal preferences don\'t matter!',
  },
  {
    q: 'What is RERA?',
    options: ['Real Estate Rental Act', 'Real Estate Regulatory Authority', 'Residential Estate Reform Agency', 'Rural Estate Registration Authority'],
    answer: 1,
    explain: 'RERA (Real Estate Regulatory Authority) was established in 2016 to bring transparency and accountability to the Indian real estate sector.',
  },
  {
    q: 'What is the typical down payment percentage banks expect for a home loan?',
    options: ['5–10%', '15–25%', '40–50%', '70–80%'],
    answer: 1,
    explain: 'Most banks finance up to 75–80% of the property value (LTV ratio), so buyers need to arrange 15–25% as down payment.',
  },
  {
    q: 'Which document is most critical when buying property in India?',
    options: ['Sale Deed', 'Aadhaar card', 'PAN card', 'Driving license'],
    answer: 0,
    explain: 'The Sale Deed is the most important document — it legally transfers ownership from seller to buyer and is registered with the government.',
  },
  {
    q: 'What is "ready reckoner rate"?',
    options: ['Bank loan interest rate', 'Government-assessed minimum property value', 'Builder\'s asking price', 'Brokerage fee'],
    answer: 1,
    explain: 'The ready reckoner rate (or circle rate) is the minimum property value set by the government for taxation purposes.',
  },
  {
    q: 'Approximately how much rental yield is considered good in Indian metro cities?',
    options: ['0.5–1%', '2–4%', '8–10%', '15%+'],
    answer: 1,
    explain: 'Indian metro cities typically offer 2–4% net rental yield. Anything above 4% is considered excellent.',
  },
  {
    q: 'Under Section 24 of the Income Tax Act, what is the maximum interest deduction for a self-occupied home loan?',
    options: ['₹50,000', '₹1,00,000', '₹2,00,000', '₹5,00,000'],
    answer: 2,
    explain: 'You can claim up to ₹2 lakh per year as interest deduction on a home loan for a self-occupied property under Section 24(b).',
  },
];

const KnowledgeQuizPage = () => {
  const [step, setStep] = useState(-1); // -1 intro, 0..N-1 questions, N result
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);

  const total = QUESTIONS.length;

  const startQuiz = () => {
    setStep(0);
    setAnswers([]);
    setPicked(null);
  };

  const handlePick = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
  };

  const next = () => {
    const updated = [...answers, picked === QUESTIONS[step].answer];
    setAnswers(updated);
    setPicked(null);
    if (step + 1 < total) setStep(step + 1);
    else setStep(total);
  };

  const score = answers.filter(Boolean).length;
  const percent = total ? Math.round((score / total) * 100) : 0;
  const grade = percent >= 90 ? { label: 'Real Estate Mogul', color: 'text-[#D4AF37]' }
    : percent >= 70 ? { label: 'Savvy Buyer', color: 'text-green-600' }
    : percent >= 50 ? { label: 'Rising Star', color: 'text-blue-600' }
    : { label: 'Keep Learning', color: 'text-amber-600' };

  const handleShare = () => {
    const text = `I scored ${score}/${total} on the Bharat Estate Knowledge Quiz! Title earned: ${grade.label}. Try it: ${window.location.href}`;
    if (navigator.share) {
      navigator.share({ title: 'Bharat Estate Knowledge Quiz', text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Result copied to clipboard!');
    }
  };

  // Intro
  if (step === -1) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="kquiz-intro">
      <Seo title="Real Estate Knowledge Quiz" description="Test your real estate IQ with 10 questions on RERA, stamp duty, home loans, and more. Earn your title." path="/knowledge-quiz" />
        <div className="container-luxury max-w-2xl">
          <div className="bg-white rounded-sm shadow-md border border-border/50 p-10 md:p-14 text-center">
            <div className="h-20 w-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6">
              <Trophy className="h-10 w-10 text-[#D4AF37]" />
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-medium mb-4">
              <Sparkles className="h-3.5 w-3.5" /> 10 questions · 5 minutes
            </div>
            <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-4">
              Real Estate Knowledge Quiz
            </h1>
            <p className="text-muted-foreground text-base mb-8 max-w-md mx-auto">
              Think you know Indian real estate? Test yourself on stamp duty, RERA, home loans, taxes &amp; more. Earn your title.
            </p>
            <Button onClick={startQuiz} className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-10 py-6 text-base" data-testid="kquiz-start-btn">
              Start the Quiz
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Result
  if (step === total) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="kquiz-result">
        <div className="container-luxury max-w-2xl">
          <div className="bg-white rounded-sm shadow-md border border-border/50 p-10 md:p-14 text-center">
            <Trophy className={`h-16 w-16 mx-auto mb-4 ${grade.color}`} />
            <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">You scored</p>
            <p className="font-['Plus_Jakarta_Sans'] text-7xl font-bold text-[#0F172A] mb-2" data-testid="kquiz-score">
              {score}<span className="text-3xl text-muted-foreground">/{total}</span>
            </p>
            <p className={`text-xl font-bold mb-2 ${grade.color}`}>{grade.label}</p>
            <p className="text-muted-foreground mb-8">{percent}% correct</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={startQuiz} className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="kquiz-retry-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Try Again
              </Button>
              <Button onClick={handleShare} variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
                <Share2 className="h-4 w-4 mr-2" /> Share Result
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question
  const q = QUESTIONS[step];
  const isAnswered = picked !== null;
  const isCorrect = picked === q.answer;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid={`kquiz-step-${step}`}>
      <div className="container-luxury max-w-2xl">
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
            <span>Question {step + 1} of {total}</span>
            <span>Score: {answers.filter(Boolean).length}</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${((step + (isAnswered ? 1 : 0)) / total) * 100}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-sm shadow-md border border-border/50 p-8 md:p-10">
          <h2 className="font-['Plus_Jakarta_Sans'] text-2xl md:text-3xl font-bold text-[#0F172A] mb-6">
            {q.q}
          </h2>

          <div className="space-y-3 mb-6">
            {q.options.map((opt, idx) => {
              const isPicked = picked === idx;
              const isAnswer = q.answer === idx;
              let cls = 'border-border hover:border-[#D4AF37] hover:bg-[#D4AF37]/5';
              if (isAnswered) {
                if (isAnswer) cls = 'border-green-500 bg-green-50';
                else if (isPicked) cls = 'border-red-500 bg-red-50';
                else cls = 'border-border opacity-60';
              }
              return (
                <button
                  key={idx}
                  onClick={() => handlePick(idx)}
                  disabled={isAnswered}
                  className={`w-full text-left p-4 rounded-sm border-2 transition-all flex items-center justify-between ${cls}`}
                  data-testid={`kquiz-opt-${step}-${idx}`}
                >
                  <span className="font-medium text-[#0F172A]">{opt}</span>
                  {isAnswered && isAnswer && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                  {isAnswered && isPicked && !isAnswer && <XCircle className="h-5 w-5 text-red-600" />}
                </button>
              );
            })}
          </div>

          {isAnswered && (
            <>
              <div className={`p-4 rounded-sm mb-4 text-sm ${isCorrect ? 'bg-green-50 text-green-900' : 'bg-amber-50 text-amber-900'}`}>
                <strong>{isCorrect ? '✓ Correct!' : '✕ Not quite.'}</strong> {q.explain}
              </div>
              <Button onClick={next} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white py-5" data-testid="kquiz-next-btn">
                {step + 1 < total ? 'Next Question' : 'See My Score'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeQuizPage;
